import { Controller } from "@hotwired/stimulus"
import { checkInStopFrom, focusCheckInStop } from "./check_in_stops"

// One instance per check-in row. Saves the row independently (per-row fetch, no
// full-page reload), flips between the entry and couldn't-locate states, and keeps
// the global "X of Y updated" counter in sync from the server's response.
export default class extends Controller {
  static values = {
    snapshotUrl: String, flagUrl: String, clearUrl: String, prior: Number, goalKey: String,
    markRemovedUrl: String, undoRemovedUrl: String, clearPrematureUrl: String, daysPlaced: Number,
    lastCheckinDays: Number, flaggedPremature: Boolean, playlistUrl: String,
    // The premature window (Placement#premature_window_days:
    // DEFAULT_TERM_PREMATURE_DAYS for a default term, the package's months
    // plus grace for a package), and the copy names the package. The row
    // always renders these; the defaults only cover a missing attribute and
    // are pinned to the Ruby constants (SPT_Premature_Window_25_Plan.md).
    prematureDays: { type: Number, default: 25 }, termLabel: { type: String, default: "" },
    termDays: { type: Number, default: 28 }, renewed: Boolean,
    // Every week's number before last week's, oldest first ({on, count}):
    // the lower-number pop-up names an exact repeat (owner 2026-10-20).
    earlier: { type: Array, default: [] },
    // A SubmitHub add (owner 2026-10-20): Removed simply ends it (the server
    // expires it whatever the day), so it never opens the flag dialog.
    submithub: Boolean
  }

  pastWindow() { return this.daysPlacedValue > this.prematureDaysValue }

  // "on day 18 of 28" (committee design SHOULD 2): the concrete day of the
  // current term, never a threshold; past the term it says so. null when
  // the row has no placed date yet. A renewed row counts from its renewal
  // and says so in the subject (diff review, words SHOULD 1).
  dayPhrase() {
    if (this.daysPlacedValue < 0) return null
    const day = Math.max(this.daysPlacedValue, 1)
    const term = this.termDaysValue
    return day <= term ? "on day " + day + " of " + term : "on day " + day + ", past its " + term + "-day term,"
  }

  subject() { return this.renewedValue ? "This renewed placement" : "This placement" }
  static targets = ["input", "entry", "flagged", "result", "last", "message", "copyFeedback",
                    "rightCol", "removedButton", "prematureNote"]

  save() {
    const raw = this.hasInputTarget ? this.inputTarget.value : ""
    if (raw === "" || isNaN(parseInt(raw, 10))) {
      this.message("Enter a total stream count, or use No change.", true)
      return
    }
    const value = parseInt(raw, 10)
    const prior = this.hasPriorValue ? this.priorValue : 0
    // Same-number tripwire (owner batch 2026-08-05 #3): typing the exact
    // prior total is a disguised No change, so after 5+ quiet days it gets
    // the same scrutiny. Client-side and advisory only; every action it
    // fires lands on a server-authoritative endpoint. Never triggers on
    // rows with no check-in history (-1), same-day corrections (0), or
    // premature-flagged rows (the red note owns that story).
    if (value === prior && this.lastCheckinDaysValue > 5 && !this.flaggedPrematureValue) {
      if (this.pastWindow()) {
        if (!this._staleNoticeShown) {
          this._staleNoticeShown = true
          this.showStaleInterstitial("save")
          return
        }
      } else if (!this._verifyAnswered) {
        this.showVerifyPopup(value)
        return
      }
    }
    // Catch a down-revision BEFORE saving: a lower-than-prior total is usually a
    // mistake, so confirm first rather than recording it silently.
    if (value < prior) {
      this.confirmDownRevision(value, prior, () => this.doSave(value))
      return
    }
    this.doSave(value)
  }

  doSave(value) {
    this.post(this.snapshotUrlValue, { total_streams: value }, (data) => this.renderSaved(data))
  }

  noChange() {
    // Past-the-window interstitial (owner batch 2026-08-05 #2): a no-change
    // on a placement past its premature window may mean the song is gone. Shown once
    // per row per page load; the second click posts normally, exactly as
    // the dialog instructs. The server never sees the first click. The
    // once-memory is SHARED with the same-number Save door: the fact
    // acknowledged ("I just checked this playlist") is about the row, not
    // the button.
    if (this.pastWindow() && !this._staleNoticeShown) {
      this._staleNoticeShown = true
      this.showStaleInterstitial("no_change")
      return
    }
    this.post(this.snapshotUrlValue, { no_change: "1" }, (data) => this.renderSaved(data))
  }

  // The final sentence is door-specific: telling a Save-path user to
  // "click the No change button again" would send them hunting for a
  // button they never pressed.
  showStaleInterstitial(door) {
    const finalSentence = door === "save"
      ? "If it is still on the playlist, click Save again to record your number."
      : "If it is still on the playlist, click the No change button again."
    this.openModal({
      title: "Check the playlist first",
      message: (this.termLabelValue
        ? "This placement is past its paid term (" + this.termLabelValue + ") and the song may have been removed, which is why you are not seeing a change from last week's check-in. "
        : this.subject() + " is " + this.dayPhrase() + " and the song may have been removed, which is why you are not seeing a change from last week's check-in. ")
        + "Please manually check the playlist to confirm. If it has been removed, click the Removed button. " + finalSentence,
      confirmLabel: "Got it",
      acknowledge: true
    })
  }

  // The verify popup for a same-number save inside the premature window
  // (owner batch 2026-08-05 #3): link out to the playlist (the popup stays
  // open), then Still live / Removed / Cancel. Still live saves the number
  // WITH the confirmation param (Sibyl FLAT, non-urgent); Removed rides
  // the existing flag path (Sibyl PRM, urgent) without a second confirm:
  // the user just made exactly that choice here.
  showVerifyPopup(value) {
    this.openModal({
      title: "Verify this playlist",
      message: (this.termLabelValue
        ? "This placement is inside its paid term (" + this.termLabelValue + ") and should still be producing. "
        : (this.dayPhrase()
          ? this.subject() + " is " + this.dayPhrase() + " and the playlist should still be producing. "
          : "This placement has no placed date yet. "))
        + "Please manually verify to see if it is removed prematurely or still active. If you choose Removed, a flag goes to the SPT lead and the playlist stays on this list until it is resolved from the Placements page.",
      link: this.playlistUrlValue
        ? { href: this.playlistUrlValue, label: "Open the playlist in a new tab" }
        : { text: "No Spotify link is on file for this playlist. Search its name in Spotify to check it." },
      buttons: [
        { label: "Still live", className: "btn btn-ghost", onClick: () => {
          this._verifyAnswered = true
          this.post(this.snapshotUrlValue, { total_streams: value, confirmed_still_live: "1" }, (data) => this.renderSaved(data))
        } },
        { label: "Removed", className: "btn btn-ghost", style: "color: var(--spt-error);", onClick: () => {
          this._verifyAnswered = true
          this.postRemoved()
        } },
        { label: "Cancel", className: "btn btn-primary", focus: true }
      ]
    })
  }

  // The Removed button (owner batch 2026-08-05 #2). Past the premature
  // window: post immediately (no confirm; the undo bar is the safety). Inside it
  // (or no placed date): confirm first, because this path escalates to the
  // SPT lead. The response's path decides the rendering either way; the
  // server branch is authoritative.
  removed() {
    if (this.pastWindow() || this.submithubValue) {
      this.postRemoved()
      return
    }
    this.openModal({
      title: "Flag for the SPT lead",
      message: (this.termLabelValue
        ? "This placement is inside its paid term (" + this.termLabelValue + "). If you confirm, a flag will be sent to the SPT lead to investigate. "
        : (this.dayPhrase()
          ? this.subject() + " is only " + this.dayPhrase() + ", so removing it now sends a flag to the SPT lead. "
          : "This placement has no placed date yet, so removing it now sends a flag to the SPT lead. "))
        + "The playlist will remain on this list with a flag until it is manually removed from the Placements page.",
      confirmLabel: "Send the flag",
      onConfirm: () => this.postRemoved()
    })
  }

  postRemoved() {
    this.post(this.markRemovedUrlValue, {}, (data) => {
      if (data.path === "expired") this.showUndoBar(data)
      else if (data.path === "flagged") this.applyFlagged(data)
      else this.updateProgress(data)
    })
  }

  // The in-row copy of the placements-page undo banner: the identity column
  // stays visible (several expiries in a row must never become anonymous
  // green bars), only the entry column swaps for the bar. The bar persists
  // for the page view (no auto-collapse: the server honors the undo for
  // 7 days, so a late click is always a valid request); the row leaves the
  // worklist naturally on the next load.
  showUndoBar(data) {
    if (this._undoBar) return
    if (this.hasEntryTarget) this.entryTarget.style.display = "none"
    if (this.hasPrematureNoteTarget) this.prematureNoteTarget.style.display = "none"

    const bar = document.createElement("div")
    bar.className = "spt-flash spt-flash-success"
    bar.style.cssText = "display: flex; justify-content: space-between; align-items: center; gap: 12px;"
    const label = document.createElement("span")
    label.textContent = "Removed and marked expired. Staff can undo this for 7 days under Recently deleted on the Placements page."
    const undoBtn = document.createElement("button")
    undoBtn.type = "button"
    undoBtn.className = "spt-btn-secondary spt-btn-sm"
    undoBtn.textContent = "Undo"
    undoBtn.addEventListener("click", () => this.undoRemoved())
    bar.appendChild(label)
    bar.appendChild(undoBtn)
    this._undoBar = bar
    if (this.hasRightColTarget) this.rightColTarget.appendChild(bar)

    this.updateProgress(data)
  }

  undoRemoved() {
    this.post(this.undoRemovedUrlValue, {}, (data) => {
      if (this._undoBar) { this._undoBar.remove(); this._undoBar = null }
      if (this.hasEntryTarget) this.entryTarget.style.display = "block"
      this.updateProgress(data)
    })
  }

  applyFlagged(data) {
    // Keep the same-number trigger's state in sync: a just-flagged row must
    // not re-offer the verify popup this session.
    this.flaggedPrematureValue = true
    this.element.classList.add("check-in-flagged-premature")
    if (this.hasPrematureNoteTarget) this.prematureNoteTarget.style.display = "block"
    if (this.hasRemovedButtonTarget) this.removedButtonTarget.style.display = "none"
    this.updateProgress(data)
  }

  clearPremature() {
    this.post(this.clearPrematureUrlValue, {}, () => {
      this.element.classList.remove("check-in-flagged-premature")
      if (this.hasPrematureNoteTarget) this.prematureNoteTarget.style.display = "none"
      if (this.hasRemovedButtonTarget) this.removedButtonTarget.style.display = ""
    })
  }

  cantFind() {
    this.post(this.flagUrlValue, {}, () => {
      if (this.hasEntryTarget) this.entryTarget.style.display = "none"
      if (this.hasFlaggedTarget) this.flaggedTarget.style.display = "block"
      this.message("", false, true)
    })
  }

  undoMissing() {
    this.post(this.clearUrlValue, {}, () => {
      if (this.hasFlaggedTarget) this.flaggedTarget.style.display = "none"
      if (this.hasEntryTarget) this.entryTarget.style.display = "block"
    })
  }

  renderSaved(data) {
    // Today's snapshot now exists: the same-number popup must not re-fire
    // on a same-session correction the user just answered.
    this.lastCheckinDaysValue = 0
    const sign = data.delta >= 0 ? "+" : ""
    if (this.hasResultTarget) {
      this.resultTarget.innerHTML = `<span class="spt-badge spt-badge-success">Updated: ${data.cumulative.toLocaleString()} (${sign}${data.delta.toLocaleString()}) on ${data.snapshot_date}</span>`
    }
    // Done now: hide the "Last total recorded" line and CLEAR the input so today's
    // number is never left sitting in the box (it must be empty for tomorrow).
    if (this.hasLastTarget) this.lastTarget.style.display = "none"
    if (this.hasInputTarget) this.inputTarget.value = ""
    this.bumpGoal(data.cumulative)
    this.message("", false, true)
    this.updateProgress(data)
  }

  // Live-update the client's goal summary line for this placement's release/group.
  // Adds only the INCREMENT since this row last contributed (baseline = the row's
  // pre-today total), so re-saving the same row does not double-count.
  bumpGoal(cumulative) {
    if (!this.hasGoalKeyValue || !this.goalKeyValue) return
    const el = document.getElementById(this.goalKeyValue)
    if (!el) return
    const base = (this._appliedCumulative === undefined)
      ? (this.hasPriorValue ? this.priorValue : 0)
      : this._appliedCumulative
    const increment = cumulative - base
    this._appliedCumulative = cumulative
    const streams = (parseInt(el.dataset.goalStreams, 10) || 0) + increment
    el.dataset.goalStreams = streams
    const goal = parseInt(el.dataset.goalTarget, 10) || 0
    const numEl = el.querySelector(".goal-streams-num")
    if (numEl) numEl.textContent = streams.toLocaleString()
    const toGoEl = el.querySelector(".goal-togo-num")
    if (toGoEl) toGoEl.textContent = Math.max(goal - streams, 0).toLocaleString()
  }

  updateProgress(data) {
    if (data.done_count !== undefined) {
      const el = document.getElementById("checkin-progress")
      if (el) el.textContent = data.done_count
    }
    if (data.total !== undefined) {
      const el = document.getElementById("checkin-total")
      if (el) el.textContent = data.total
    }
  }

  message(text, isError, hide) {
    if (!this.hasMessageTarget) return
    if (hide || !text) {
      this.messageTarget.style.display = "none"
      this.messageTarget.textContent = ""
      return
    }
    this.messageTarget.textContent = text
    this.messageTarget.className = isError ? "spt-helper spt-helper-warning" : "spt-helper"
    this.messageTarget.style.display = "block"
  }

  post(url, body, onOk) {
    fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", "Accept": "application/json", "X-CSRF-Token": this.csrf() },
      body: JSON.stringify(body)
    })
      .then(r => (r.ok ? r.json() : Promise.reject(r)))
      .then(data => { if (data && data.ok) onOk(data); else this.message((data && data.error) || "Could not save. Try again.", true) })
      .catch(() => this.message("Could not save. Check your connection and try again.", true))
  }

  csrf() {
    const m = document.querySelector('meta[name="csrf-token"]')
    return m ? m.getAttribute("content") : ""
  }

  // Copy the exact playlist NAME (for pasting into S4A search) - distinct from the
  // link copy which copies the Spotify URL.
  copyName(event) { this.copyText(event.currentTarget.dataset.name || "", "name copied") }
  copyLink(event) { this.copyText(event.currentTarget.dataset.url || "", "link copied") }

  copyText(text, label) {
    const done = () => this.flashCopied(label)
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(done).catch(() => this.fallbackCopy(text, done))
    } else {
      this.fallbackCopy(text, done)
    }
  }

  fallbackCopy(text, done) {
    const ta = document.createElement("textarea")
    ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0"
    document.body.appendChild(ta); ta.select()
    try { document.execCommand("copy") } catch (e) { /* no-op */ }
    ta.remove(); done()
  }

  flashCopied(label) {
    if (!this.hasCopyFeedbackTarget) return
    this.copyFeedbackTarget.textContent = label
    this.copyFeedbackTarget.style.display = "inline"
    clearTimeout(this._copyTimer)
    this._copyTimer = setTimeout(() => { this.copyFeedbackTarget.style.display = "none" }, 1500)
  }

  // Enter saves the row; Tab moves to the next row's input (skipping the buttons).
  // One handler so it does not depend on the Stimulus keydown.tab key filter.
  onKeydown(event) {
    if (event.key === "Enter") {
      event.preventDefault()
      this.save()
    } else if (event.key === "Tab") {
      this.tabToNext(event)
    }
  }

  // Tab moves between stream inputs in visual row order (skipping the buttons), so
  // entry is mouse-free: type, Enter to save, Tab to the next row. A client
  // card's unanswered notice question is a stop on the same path, after the
  // card's last input (check_in_stops.js holds the one list).
  tabToNext(event) {
    const next = checkInStopFrom(this.inputTarget, event.shiftKey)
    if (next) { event.preventDefault(); focusCheckInStop(next) }
  }

  // A lower number means Spotify removed plays from OUR playlist (which can
  // mean it was flagged) or a typo (owner 2026-10-20). Saying which number
  // it repeats catches the old-week typo; "Go back and check" keeps the
  // focus, so a fast Enter never saves the lower number.
  confirmDownRevision(value, prior, onConfirm) {
    const n = (x) => x.toLocaleString()
    // Never for a 0: a drop to 0 is the strongest sign of removed plays.
    const repeat = value > 0 && this.earlierValue.slice().reverse().find((e) => e.count === value)
    this.openModal({
      title: "Lower than last week",
      message: `${n(value)} is lower than last week's ${n(prior)}. If that is right, Spotify removed plays from this playlist and Sibyl will flag it. Check the number before you save.`,
      link: repeat ? { text: `${n(value)} is exactly the number entered on ${repeat.on}.` } : null,
      buttons: [
        { label: `Save ${n(value)} anyway`, onClick: onConfirm },
        { label: "Go back and check", className: "btn btn-primary", focus: true,
          onClick: () => { this.inputTarget.focus(); this.inputTarget.select() } }
      ]
    })
  }

  // Native in-app confirm built from the app's existing .sh-modal styles. Default
  // focus is Cancel, so a fast Enter never auto-commits the lower number.
  // acknowledge: true renders a single primary button (focused) that just
  // dismisses; used by the past-the-window interstitial.
  // link: { href, label } renders a new-tab link between the message and
  // the buttons (the modal stays open); { text } renders a plain fallback
  // line instead. buttons: a custom list [{ label, className, onClick,
  // focus }] replacing the confirm/cancel pair; every button closes the
  // modal, a missing onClick is a plain Cancel. Escape and backdrop always
  // equal Cancel (close, nothing fires).
  openModal({ title, message, confirmLabel, onConfirm, acknowledge = false, link = null, buttons = null }) {
    const overlay = document.createElement("div")
    overlay.className = "sh-modal"
    const backdrop = document.createElement("div")
    backdrop.className = "sh-modal-backdrop"
    const content = document.createElement("div")
    content.className = "sh-modal-content"
    const h = document.createElement("h3")
    h.className = "sh-modal-title"
    h.textContent = title
    const p = document.createElement("p")
    p.className = "sh-modal-message"
    p.textContent = message
    const actions = document.createElement("div")
    actions.className = "sh-modal-actions"

    const close = () => { overlay.remove(); document.removeEventListener("keydown", onKey) }
    const onKey = (e) => { if (e.key === "Escape") close() }

    let linkRow = null
    if (link) {
      linkRow = document.createElement("p")
      linkRow.className = "sh-modal-message"
      if (link.href) {
        const a = document.createElement("a")
        a.href = link.href
        a.target = "_blank"
        a.rel = "noopener noreferrer"
        a.textContent = link.label
        linkRow.appendChild(a)
      } else {
        linkRow.textContent = link.text
        linkRow.style.color = "var(--spt-text-secondary, #6b7280)"
      }
    }

    let focusBtn
    if (buttons) {
      buttons.forEach((spec) => {
        const btn = document.createElement("button")
        btn.type = "button"
        btn.className = spec.className || "btn btn-ghost"
        if (spec.style) btn.style.cssText = spec.style
        btn.textContent = spec.label
        btn.addEventListener("click", () => { close(); if (spec.onClick) spec.onClick() })
        actions.appendChild(btn)
        if (spec.focus) focusBtn = btn
      })
      focusBtn = focusBtn || actions.lastChild
    } else if (acknowledge) {
      const okBtn = document.createElement("button")
      okBtn.type = "button"
      okBtn.className = "btn btn-primary"
      okBtn.textContent = confirmLabel
      okBtn.addEventListener("click", () => { close(); if (onConfirm) onConfirm() })
      actions.appendChild(okBtn)
      focusBtn = okBtn
    } else {
      const continueBtn = document.createElement("button")
      continueBtn.type = "button"
      continueBtn.className = "btn btn-ghost"
      continueBtn.textContent = confirmLabel
      continueBtn.addEventListener("click", () => { close(); onConfirm() })

      const cancelBtn = document.createElement("button")
      cancelBtn.type = "button"
      cancelBtn.className = "btn btn-primary"
      cancelBtn.textContent = "Cancel"
      cancelBtn.addEventListener("click", close)

      actions.appendChild(continueBtn)
      actions.appendChild(cancelBtn)
      focusBtn = cancelBtn
    }

    content.appendChild(h)
    content.appendChild(p)
    if (linkRow) content.appendChild(linkRow)
    content.appendChild(actions)
    overlay.appendChild(backdrop)
    overlay.appendChild(content)
    backdrop.addEventListener("click", close)
    document.addEventListener("keydown", onKey)
    document.body.appendChild(overlay)
    focusBtn.focus()
  }
}
